/**
 * React Query hooks for /api/v1/earthworks/* (EW-1).
 *
 * EVERY HOOK RETURNS THE META: the holdback label and the volume unit come
 * from it. EVERY KEY STARTS ['earthworks'] and every write invalidates that
 * one prefix. EVERY READ SPREADS LOAD_FAILURE_QUERY_OPTIONS, so a refusal is
 * not retried and reads as a refusal, never as an empty list.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type {
  EwCatalog,
  EwCrewWrite,
  EwCustomerSettings,
  EwCustomerSettingsUpdate,
  EwJob,
  EwJobDetail,
  EwEmployeeHours,
  EwJobEquipmentWrite,
  EwJobWrite,
  EwMeta,
  EwNumbering,
  EwNumberingUpdate,
  EwRate,
  EwRateWrite,
  EwResult,
  EwSettings,
  EwSettingsUpdate,
  EwSite,
  EwSiteDetail,
  EwSiteWrite,
  EwTruckBilling,
  EwTruckBillingBasis,
} from '../types/earthworks';

/** Meta did not arrive: no zone, rather than a guessed one (same idiom as usePipelineFacility). */
const NO_ZONE: string | null = null;

const EMPTY_META: EwMeta = {
  unitSystem: null,
  unitSystemSource: null,
  units: null,
  currency: null,
  country: null,
  holdbackLabel: null,
  timeZone: NO_ZONE,
  today: null,
};

/** Split a body into payload and meta. `data: null` stays null — never the envelope. */
export function unwrapEw<T>(body: unknown): EwResult<T | null> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_META };
  const b = body as { data?: unknown; meta?: Partial<EwMeta> };
  const data = 'data' in b && b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_META, ...(b.meta ?? {}) } };
}

/** A list read: a non-array payload becomes `[]` rather than reaching `.map`. */
export function unwrapEwList<T>(body: unknown): EwResult<T[]> {
  const { data, meta } = unwrapEw<unknown>(body);
  return { data: Array.isArray(data) ? (data as T[]) : [], meta };
}

export interface EwSiteFilters {
  kind?: string;
  customerId?: string;
  status?: string;
  q?: string;
}

export interface EwJobFilters {
  status?: string;
  jobType?: string;
  siteId?: string;
  customerId?: string;
  q?: string;
}

export interface EwRateFilters {
  customer_id?: string;
  kind?: string;
  on?: string;
}

const clean = (p?: object) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = String(v);
  return Object.keys(out).length ? out : undefined;
};

export const ewKeys = {
  all: ['earthworks'] as const,
  meta: () => ['earthworks', 'meta'] as const,
  settings: () => ['earthworks', 'settings'] as const,
  numbering: () => ['earthworks', 'numbering'] as const,
  customerSettings: (id: string) => ['earthworks', 'customerSettings', id] as const,
  customers: () => ['earthworks', 'customers'] as const,
  users: () => ['earthworks', 'users'] as const,
  equipment: () => ['earthworks', 'equipment'] as const,
  sites: (p?: EwSiteFilters) => ['earthworks', 'sites', clean(p) ?? {}] as const,
  site: (id: string) => ['earthworks', 'site', id] as const,
  jobs: (p?: EwJobFilters) => ['earthworks', 'jobs', clean(p) ?? {}] as const,
  job: (id: string) => ['earthworks', 'job', id] as const,
  rates: (p?: EwRateFilters) => ['earthworks', 'rates', clean(p) ?? {}] as const,
  rate: (id: string) => ['earthworks', 'rate', id] as const,
};

export const invalidateEw = (qc: QueryClient) => qc.invalidateQueries({ queryKey: ewKeys.all });

/** Every write invalidates the whole prefix: a crew add moves the register count and the detail. */
function useEwWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateEw(qc) });
}
/** The same write hook for sibling hook files (EW-9 inventory). */
export const useEwWriteFn = useEwWrite;

/** The catalog (job types, kinds, statuses, crew roles, price kinds/units) with the API's labels. */
export function useEwMeta() {
  return useQuery({
    queryKey: ewKeys.meta(),
    queryFn: async () => unwrapEw<EwCatalog>((await api.earthworks.meta()).data),
    staleTime: 5 * 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwSettings() {
  return useQuery({
    queryKey: ewKeys.settings(),
    queryFn: async () => unwrapEw<EwSettings>((await api.earthworks.settings.get()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useUpdateEwSettings = () =>
  useEwWrite(async (body: EwSettingsUpdate) => unwrapEw<EwSettings>((await api.earthworks.settings.update(body)).data));

/** Numbering per series. Only admin / office may read it, so it is fetched only when `enabled`. */
export function useEwNumbering(enabled: boolean) {
  return useQuery({
    queryKey: ewKeys.numbering(),
    queryFn: async () => unwrapEw<EwNumbering>((await api.earthworks.settings.numbering.get()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useUpdateEwNumbering = () =>
  useEwWrite(async (body: EwNumberingUpdate) => unwrapEw<EwNumbering>((await api.earthworks.settings.numbering.update(body)).data));

export function useEwCustomerSettings(customerId: string | undefined) {
  return useQuery({
    queryKey: ewKeys.customerSettings(customerId ?? ''),
    queryFn: async () => unwrapEw<EwCustomerSettings>((await api.earthworks.customerSettings.get(customerId as string)).data),
    enabled: !!customerId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useUpdateEwCustomerSettings = () =>
  useEwWrite(async ({ customerId, body }: { customerId: string; body: EwCustomerSettingsUpdate }) =>
    unwrapEw<EwCustomerSettings>((await api.earthworks.customerSettings.update(customerId, body)).data));

/** The tenant's customers, for the site and job pickers. */
export function useEwCustomers(enabled = true) {
  return useQuery({
    queryKey: ewKeys.customers(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; companyName?: string | null }>((await api.customers.list()).data)
        .map((c) => ({ id: c.id, name: c.name || c.companyName || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's people (not terminated), for the crew and supervisor pickers. */
export function useEwUsers(enabled = true) {
  return useQuery({
    queryKey: ewKeys.users(),
    queryFn: async () =>
      unwrapArray<{ id: string; firstName?: string; lastName?: string; fullName?: string; role?: string;
        first_name?: string; last_name?: string; employmentStatus?: string; employment_status?: string }>((await api.users.list()).data)
        .filter((u) => (u.employmentStatus ?? u.employment_status) !== 'terminated')
        .map((u) => ({
          id: u.id,
          name: (u.fullName || [u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ')).trim() || '—',
          role: u.role ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's owned equipment (not retired), for the job equipment picker. */
export function useEwEquipmentOptions(enabled = true) {
  return useQuery({
    queryKey: ewKeys.equipment(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; unitNumber?: string | null; unit_number?: string | null;
        serialNumber?: string | null; serial_number?: string | null; status?: string | null }>((await api.equipment.list()).data)
        .filter((e) => e.status !== 'retired')
        .map((e) => ({
          id: e.id,
          name: e.name || '—',
          unitNumber: e.unitNumber ?? e.unit_number ?? null,
          serialNumber: e.serialNumber ?? e.serial_number ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── Sites ───────────────────────────────────────────────────────────────────

export function useEwSites(filters?: EwSiteFilters) {
  return useQuery({
    queryKey: ewKeys.sites(filters),
    queryFn: async () => unwrapEwList<EwSite>((await api.earthworks.sites.list(clean(filters))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwSite(id: string | undefined) {
  return useQuery({
    queryKey: ewKeys.site(id ?? ''),
    queryFn: async () => unwrapEw<EwSiteDetail>((await api.earthworks.sites.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateEwSite = () =>
  useEwWrite(async (body: EwSiteWrite) => unwrapEw<EwSiteDetail>((await api.earthworks.sites.create(body)).data));
export const useUpdateEwSite = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwSiteWrite }) =>
    unwrapEw<EwSiteDetail>((await api.earthworks.sites.update(id, body)).data));
export const useDeleteEwSite = () => useEwWrite(async (id: string) => (await api.earthworks.sites.remove(id)).data);

// ─── Jobs, crew, equipment ───────────────────────────────────────────────────

export function useEwJobs(filters?: EwJobFilters) {
  return useQuery({
    queryKey: ewKeys.jobs(filters),
    queryFn: async () => unwrapEwList<EwJob>((await api.earthworks.jobs.list(clean(filters))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwJob(id: string | undefined) {
  return useQuery({
    queryKey: ewKeys.job(id ?? ''),
    queryFn: async () => unwrapEw<EwJobDetail>((await api.earthworks.jobs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Employee Hours on one job (clock-in vs daily log). `range` absent = the API's last two weeks. */
export function useEwJobEmployeeHours(id: string | undefined, range: { from?: string; to?: string }) {
  return useQuery({
    queryKey: ['earthworks', 'job-employee-hours', id ?? '', clean(range) ?? {}] as const,
    queryFn: async () => unwrapEw<EwEmployeeHours>((await api.earthworks.jobs.employeeHours(id as string, clean(range))).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** One worker's Employee Hours across every earthworks job. */
export function useEwEmployeeHours(userId: string | undefined, range: { from?: string; to?: string }) {
  return useQuery({
    queryKey: ['earthworks', 'employee-hours', userId ?? '', clean(range) ?? {}] as const,
    queryFn: async () => unwrapEw<EwEmployeeHours>((await api.earthworks.employees.hours(userId as string, clean(range))).data),
    enabled: !!userId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateEwJob = () =>
  useEwWrite(async (body: EwJobWrite) => unwrapEw<EwJobDetail>((await api.earthworks.jobs.create(body)).data));
export const useUpdateEwJob = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwJobWrite }) =>
    unwrapEw<EwJobDetail>((await api.earthworks.jobs.update(id, body)).data));
/** 0600: PUT /earthworks/jobs/:id/billing — the job's truck billing basis. */
export const useSetEwTruckBilling = () =>
  useEwWrite(async ({ id, basis }: { id: string; basis: EwTruckBillingBasis }) =>
    unwrapEw<EwTruckBilling>((await api.earthworks.jobs.setTruckBilling(id, { truck_billing_basis: basis })).data));
export const useDeleteEwJob = () => useEwWrite(async (id: string) => (await api.earthworks.jobs.remove(id)).data);

export const useAddEwCrew = () =>
  useEwWrite(async ({ jobId, body }: { jobId: string; body: EwCrewWrite }) => (await api.earthworks.crew.add(jobId, body)).data);
export const useRemoveEwCrew = () => useEwWrite(async (id: string) => (await api.earthworks.crew.remove(id)).data);

export const useAddEwJobEquipment = () =>
  useEwWrite(async ({ jobId, body }: { jobId: string; body: EwJobEquipmentWrite }) =>
    (await api.earthworks.jobEquipment.add(jobId, body)).data);
export const useRemoveEwJobEquipment = () =>
  useEwWrite(async (id: string) => (await api.earthworks.jobEquipment.remove(id)).data);

// ─── Rate card ───────────────────────────────────────────────────────────────

export function useEwRates(filters?: EwRateFilters, enabled = true) {
  return useQuery({
    queryKey: ewKeys.rates(filters),
    queryFn: async () => unwrapEwList<EwRate>((await api.earthworks.rates.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwRate(id: string | undefined) {
  return useQuery({
    queryKey: ewKeys.rate(id ?? ''),
    queryFn: async () => unwrapEw<EwRate>((await api.earthworks.rates.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateEwRate = () =>
  useEwWrite(async (body: EwRateWrite) => unwrapEw<EwRate>((await api.earthworks.rates.create(body)).data));
export const useUpdateEwRate = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwRateWrite }) =>
    unwrapEw<EwRate>((await api.earthworks.rates.update(id, body)).data));
export const useDeleteEwRate = () => useEwWrite(async (id: string) => (await api.earthworks.rates.remove(id)).data);

/** The tenant's equipment types, for an equipment or trucking rate. */
export function useEwEquipmentTypes(enabled = true) {
  return useQuery({
    queryKey: ['earthworks', 'equipmentTypes'] as const,
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null }>((await api.equipmentTypes.list()).data)
        .map((t) => ({ id: t.id, name: t.name || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
